import { getCurrentInstance, inject } from 'vue'
import type { ConversionSubject, TrackAction } from 'fundraising-core'

/**
 * The lookup half of the funnel tracking (useFundraisingTracking has the rest): what a step or a
 * submit needs to report, without the event code itself, so an island that is never tracked (a
 * campaign page, the quiz until a flow opens) doesn't load it.
 */
export interface FundraisingTracking {
  /** `start` goes out once, ahead of the first event the donor causes. */
  track: (action: Exclude<TrackAction, 'start'>, value?: number) => void
  /** What a conversion note is keyed on; the same values `track` sends. `undefined` outside a flow. */
  subject: () => ConversionSubject | undefined
}

export const FUNDRAISING_TRACKING_KEY = 'fundraisingTracking'
export const FUNDRAISING_TRACKING_NAME_KEY = 'fundraisingTrackingName'
export const FUNDRAISING_TRACKING_ORG_KEY = 'fundraisingTrackingOrg'
/** A `() => boolean` from `useRedirectFlow`: true when the host leaves the page on `complete`, so
 *  the next page runs the conversion check, not the result shown on the way out. */
export const FUNDRAISING_DEFERS_CONVERSION_KEY = 'fundraisingDefersConversion'

// A form mounted without its flow has no form type to report under, so it stays silent.
const untracked: FundraisingTracking = { track: () => {}, subject: () => undefined }

/** The nearest flow's tracking context. */
export function useFundraisingTracking(): FundraisingTracking {
  return getCurrentInstance() ? inject(FUNDRAISING_TRACKING_KEY, untracked) : untracked
}
